import React, { useEffect, useMemo, useState } from 'react';
import AssessmentContent from '@/components/pages/assessments/AssessmentContent';
import CustomStyles from '@/components/pages/assessments/common/CustomStyles';
import i18nConfig from '@/components/pages/assessments/config/i18n.json';
import setupConfig from '@/components/pages/assessments/config/config.json';
import displayConfig from '@/components/pages/assessments/config/display.js';
import stylesConfig from '@/components/pages/assessments/config/styles.js';
import tagTranslations from '@/assets/i18n/tags';
import { I18nProvider } from '@/components/pages/assessments/context/i18n.context';
import { StepProvider } from '@/components/pages/assessments/context/step.context';
import { DisplayProvider } from '@/components/pages/assessments/context/display.context';
import { LoggingProvider } from './context/logging.context';
import { UserProvider } from './context/user.context';
import {
  timestamp,
  getInvitationData,
  STATUS_UNSTARTED,
  INVITATION_ASSESSMENTS,
  ASSESSMENT_URL_PARAMETERS,
  ASSESSMENT_CHECK_DATA_FIELDS
} from '@/components/pages/assessments/util';
import Skeleton from 'react-loading-skeleton';
import NotFound from './common/NotFound';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import Auth from '@/utils/api/odas/auth';
import { isSuccessResponse } from '@/constants/httpStatus';
import { useRouter } from 'next/router';

const AssessmentPage = ({ slug, api, locale, user, components, logging, isSessionRetrieved, persistReferralClient, redirects, query }) => {
  const router = useRouter();
  const { clientId } = logging.reqParams;
  const { assessmentViewed: trackAssessmentViewed } = logging.events;
  const [assessment, setAssessment] = useState(null);
  const [status, setStatus] = useState(null);
  const [checkData, setCheckData] = useState({});
  const [metadata, setMetadata] = useState(null);
  const [translations, setTranslations] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [fetchedAnswers, setFetchedAnswers] = useState(null);
  const [answers, setAnswers] = useState({});
  const [screenData, setScreenData] = useState({});
  const [started, setStarted] = useState(timestamp());
  const [agencyClient, setAgencyClient] = useState(null);
  const [isAgencyClientRetrieved, setIsAgencyClientRetrieved] = useState(false);
  const [holdStep, setHoldStep] = useState(false);
  const [complete, setComplete] = useState(false);
  const [requiresLogout, setRequiresLogout] = useState(false);
  const [trackedViewForId, setTrackedViewForId] = useState(null);
  const [userAssessmentId, setUserAssessmentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const IS_INVITATION_ASSESSMENT = INVITATION_ASSESSMENTS.includes(slug);
  const isPro = !!user?.is_professional;
  const invitation = getInvitationData(query);

  const setAgencyClientData = (client) => {
    setAgencyClient(client);
    if (client) {
      persistReferralClient(client); // pass up to handle starting referrals
    }
  };

  useEffect(() => {
    if (slug && assessment?.id && trackedViewForId !== assessment.id) {
      trackAssessmentViewed({ assessmentId: assessment.id });
      setTrackedViewForId(assessment.id); // do this only once (for this assessment)
    }
  }, [assessment?.id, slug]);

  useEffect(() => {
    if (requiresLogout) setRequiresLogout(false);
  }, [user?.id]);

  useEffect(() => {
    // use a URL parameter to trigger starting a new instance of an assessment by calling the restart endpoint and re-fetching the assessment via slug (to get the new state of the assessment)
    if (query.startNew == 'true' && user.id) {
      setLoading(true);
      const opts = Object.assign({ locale, include_metadata: true }, pick(query || {}, ASSESSMENT_URL_PARAMETERS));
      api
        .getAssessmentBySlug(slug, agencyClient?.id, opts)
        .then((result) => api.restartAssessment(result.data.assessment.id, agencyClient?.id, invitation))
        .then((result) => {
          router.replace({ pathname: router.pathname, query: { ...omit(query, 'startNew') } }, undefined, { shallow: true });
        })
        .catch((e) => {
          router.replace({ pathname: router.pathname, query: { ...omit(query, 'startNew') } }, undefined, { shallow: true });
        });
    }
  }, [query?.startNew, user?.id]);

  // load the assessment data
  useEffect(() => {
    setLoading(true);
    // before loading the assessment data, wait for information needed in retrieving the assessment and setting up the initial state
    if (!isSessionRetrieved || !isAgencyClientRetrieved) return; // wait for information about the user and maybe an agency client
    if (!slug) return; // slug has not been read from the url path yet
    if (requiresLogout) return; // the current user session does not match the invitation url parameters
    if (query.startNew == 'true') return;
    if (clientId && !user?.id) return; // agency client assessments require auth

    setHoldStep(true);
    const done = () => setHoldStep(false); // needs to be called when finished here

    (async () => {
      try {
        const opts = Object.assign({ locale, include_metadata: true }, pick(query || {}, ASSESSMENT_URL_PARAMETERS));
        const result = await api.getAssessmentBySlug(slug, agencyClient?.id, opts);

        if (!result?.data) {
          setNotFound(true);
          return done();
        } else if (notFound) {
          setNotFound(false);
        }

        if (result.data.type === 'account_missmatch') {
          components().prompts.modal.require_logout();
          setRequiresLogout(true);
          return done();
        }

        // This forces redirect for invitation assessments
        if (IS_INVITATION_ASSESSMENT && result.data.type && result.data.message) {
          redirects.error(result.data.message);
          return done();
        } else if (IS_INVITATION_ASSESSMENT && !result.data?.ext_id_check && !Auth.isLoggedIn()) {
          redirects.error();
          return done();
        }

        setStatus(result.data.status || STATUS_UNSTARTED);
        setAssessment(result.data.assessment);
        setCheckData(Object.assign({}, pick(result.data, ASSESSMENT_CHECK_DATA_FIELDS), opts));
        setMetadata(Object.assign({}, setupConfig[slug] || {}, { css: stylesConfig[slug] || '' }, result.data.metadata || {}));
        setTranslations(merge(result.data.translations || i18nConfig, tagTranslations));

        let startedResult = null;
        if (result.data.user_assessment_id) {
          setUserAssessmentId(result.data.user_assessment_id);
        } else if (user?.id) {
          // If the person authenticated after the assessment loaded then we would not yet have a user assessment, so
          // we will try to create one and then store it.
          // This is no-op if person is unauthenticated.
          // If the person got here by invitation and has answered the questions (and stored on front end), then this will
          // pass the invitation data now that they are authenticated (and mark the assessment as started.)
          startedResult = await api.markAssessmentStarted(result.data.assessment.id, agencyClient?.id, invitation);
          if (startedResult?.data?.user_assessment_id) {
            setUserAssessmentId(startedResult.data.user_assessment_id);
          }
        }
        const answerKeys = Object.keys(answers);

        if (user?.id) {
          if (answerKeys.length) {
            // The UserAssessment should already exist if the person is authenticated.
            // Our main use case here is when a non-logged-in person completes the assessment, then signs in/up, and that will
            // cause this effect to run again now that user.id has changed.
            // This will save the answers we have been holding on to
            await api.saveAllAssessmentAnswers({
              userAssessmentId: result?.data?.user_assessment_id || startedResult?.data?.user_assessment_id,
              agencyClientId: agencyClient?.id,
              assessmentId: result.data.assessment.id,
              data: answerKeys.map((answerKey) => ({
                questionId: answers[answerKey].question_id,
                answer: answers[answerKey].value
              }))
            });
          }
          setFetchedAnswers(await api.fetchAnswersFromAssessment(result.data.assessment.id, result.data.user_assessment_id, agencyClient?.id));
        } else {
          setFetchedAnswers({});
          if (complete) {
            setComplete(false);
          }
        }
        setLoading(false);
        done();
      } catch (ex) {
        redirects.error();
        throw ex;
      }
    })();
  }, [slug, user?.id, locale, agencyClient?.id, started, isSessionRetrieved, isAgencyClientRetrieved, requiresLogout, query?.startNew]);

  const loadAgencyClient = async (clientId) => {
    // load the agency client data if completing the assessment on behalf of a client
    if (!clientId) {
      setIsAgencyClientRetrieved(true);
      return setAgencyClientData(null);
    }
    if (!user?.id) return; // agency client requires auth

    const res = await api.getAgencyClient(clientId);
    if (!isSuccessResponse(res?.status)) {
      setNotFound(true);
      return;
    }
    setAgencyClientData(res.data?.agency_client || null);
    setIsAgencyClientRetrieved(true);
  };

  useEffect(() => {
    // wait to hear about the current session before loading the agency client
    if (!isSessionRetrieved) return;
    loadAgencyClient(clientId);
  }, [clientId, user?.id, isSessionRetrieved]);

  // Wrap getAssessmentResults so that any results screen (custom or default) automatically
  // refreshes the referral client after the UserAssessment is marked finished. This ensures
  // completed_assessments in localStorage stays current regardless of which results component
  // is rendered. Any current or future results component gets the behaviour automatically from here
  // without needing to know about it (ResultsInterface, ResultsScreen, etc.).
  const apiWithClientRefresh = useMemo(() => {
    if (!clientId) return api;
    return {
      ...api,
      getAssessmentResults: async (...args) => {
        const result = await api.getAssessmentResults(...args);
        await api
          .getAgencyClient(clientId)
          .then((res) => {
            if (res?.data?.agency_client) setAgencyClientData(res.data.agency_client);
          })
          .catch(() => {});
        return result;
      }
    };
  }, [api, clientId]);

  if (requiresLogout) return null;

  if (notFound) return <NotFound translations={translations} />;

  const startNow = () => setStarted(timestamp());

  const restartAssessment = async () => {
    if (assessment) {
      const res = await api.restartAssessment(assessment.id, agencyClient?.id, invitation);
      return res.data?.user_assessment_id;
    }
    return null;
  };

  return (
    <LoggingProvider api={logging}>
      <I18nProvider locale={locale} translations={translations}>
        <DisplayProvider display={displayConfig[slug]} layout={setupConfig[slug]?.options?.layout || 'basic'}>
          <UserProvider
            user={user}
            agencyClient={agencyClient}
            setAgencyClient={setAgencyClientData}
            isPro={isPro}
            isAgencyClientRetrieved={isAgencyClientRetrieved}
          >
            <StepProvider
              fetchedAnswers={fetchedAnswers}
              setFetchedAnswers={setFetchedAnswers}
              assessment={assessment}
              checkData={checkData}
              setCheckData={setCheckData}
              metadata={metadata}
              status={status}
              slug={slug}
              api={apiWithClientRefresh}
              components={components}
              startNow={startNow}
              answers={answers}
              setAnswers={setAnswers}
              screenData={screenData}
              setScreenData={setScreenData}
              holdStep={holdStep}
              setHoldStep={setHoldStep}
              complete={complete}
              setComplete={setComplete}
              invitation={invitation}
              restartAssessment={restartAssessment}
              userAssessmentId={userAssessmentId}
              setUserAssessmentId={setUserAssessmentId}
            >
              {assessment && metadata && !loading ? <AssessmentContent /> : <Skeleton height={400} highlightColor={'#ffffff'} />}
              {<CustomStyles cssString={metadata?.css} />}
            </StepProvider>
          </UserProvider>
        </DisplayProvider>
      </I18nProvider>
    </LoggingProvider>
  );
};

export default AssessmentPage;
